import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Tabs,
  Tab,
  Toolbar,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../contexts/ThemeContext';
import { LanguageConstants } from '../helpers/LanguageConstants';
import { useChannelAdmin } from '../hooks/useChannelAdmin';
import MembersTab from './ChannelSettings/MembersTab';
import EmailConfigTab from './ChannelSettings/EmailConfigTab';
import AppsTab from './ChannelSettings/AppsTab';
import NotificationsTab from './ChannelSettings/NotificationsTab';
import AutomationTab from './ChannelSettings/AutomationTab';
import SkillsTab from './ChannelSettings/SkillsTab';
import WorkflowCanvasTab, { LegacyWorkflowCanvasTab, type WorkflowCanvasTabHandle } from './ChannelSettings/WorkflowCanvas/WorkflowCanvasTab';
import { WorkflowErrorBoundary } from './ChannelSettings/WorkflowCanvas/WorkflowErrorBoundary';
import {
  type ChannelSettingsTabId,
  getTabIndexById,
  getVisibleChannelSettingsTabs,
  resolveInitialTabId,
} from './ChannelSettings/channelSettingsTabs';



interface ChannelSettingsProps {
  open: boolean;
  onClose: () => void;
  channelId?: string;
  channelName?: string;
  channelDescription?: string;
  /** Channel tags from channel API (e.g. category labels) */
  channelTags?: string[];
  /** Channel subtags from channel API (e.g. sub-category labels) */
  channelSubtags?: string[];
  channelBusinessRules?: string;
  isChannelArchived?: boolean;
  channelType?: 'email' | 'message' | 'voice';
  workspaceTag?: string;
  workspaceId?: string;
  /** @deprecated Prefer initialTabId — legacy numeric tab index */
  initialTab?: number;
  /** Tab to open when the settings drawer opens (resolved against visible tabs) */
  initialTabId?: ChannelSettingsTabId;
  /** When the drawer opens landing on the Config tab (initialTabId="config"), also auto-opens
   * that tab's own Edit dialog — the same mechanism AutomationTab's "onOpenChannelConfigEdit"
   * already triggers internally, just made reachable from an external caller (e.g. a channel
   * list's "Edit" menu item) instead of only from within an already-open drawer. No-op if the
   * resolved initial tab isn't "config". */
  initialOpenConfigEdit?: boolean;
  /** Opens the create-skill dialog when the drawer lands on the Skills tab. */
  initialOpenAddSkill?: boolean;
  /** Called after the deep-link add-skill request has been consumed. */
  onInitialAddSkillOpened?: () => void;
  /** Called after a successful Channel Config edit — parent should re-fetch channel details. */
  onChannelUpdated?: () => void;
  /** business_rules_workflow_count — lets the Business Rules Workflow tab's canvas-header
   * "Runs: N" label close this Settings drawer and open the Channel Operations Dashboard instead
   * (a sibling drawer owned by the same parent as this component — e.g. ThreadDetailPage, which
   * already has a handler that does exactly that for its own Dashboard icon). Optional: omitted
   * entirely, the label just isn't clickable. */
  onOpenDashboard?: () => void;
}



const tabPanelId = (tabId: ChannelSettingsTabId) => `channel-settings-tabpanel-${tabId}`;
const tabButtonId = (tabId: ChannelSettingsTabId) => `channel-settings-tab-${tabId}`;



const ChannelSettings: React.FC<ChannelSettingsProps> = ({
  open,
  onClose,
  channelId,
  channelName,
  channelDescription,
  channelTags,
  channelSubtags,
  channelBusinessRules,
  isChannelArchived = false,
  channelType,
  workspaceTag,
  workspaceId,
  initialTab = 0,
  initialTabId,
  initialOpenConfigEdit = false,
  initialOpenAddSkill = false,
  onInitialAddSkillOpened,
  onChannelUpdated,
  onOpenDashboard,
}) => {
  const { isChannelAdmin, loading: adminLoading } = useChannelAdmin(channelId);
  const visibleTabs = useMemo(
    () => getVisibleChannelSettingsTabs(isChannelAdmin, channelType),
    [isChannelAdmin, channelType]
  );


  // Initialize the selected tab from the deep-link props so the drawer does not render Config
  // for one frame before switching to the requested tab (notably the Add Skill redirect).
  const initialTabForRender = initialOpenAddSkill ? 'skills' : initialTabId;
  const [activeTabIndex, setActiveTabIndex] = useState(() =>
    getTabIndexById(
      visibleTabs,
      resolveInitialTabId({
        initialTabId: initialTabForRender,
        initialTab,
        isChannelAdmin,
      })
    )
  );
  const [openConfigEditRequest, setOpenConfigEditRequest] = useState(false);
  const muiTheme = useMuiTheme();
  useTheme(); // theme context available for child tabs

  // Editing a recipient on the Business Rules Workflow tab persists a freshly-recomputed rules
  // text on the backend, but the Config tab (EmailConfigTab) is a sibling tab that stays mounted
  // (just hidden) the whole time this drawer is open — it never re-fetches on its own, so it kept
  // showing whatever channelBusinessRules this drawer was opened with until the whole drawer was
  // closed and reopened. This local copy lets the workflow tab push a fresh value in immediately
  // (optimistic — no round trip needed), while still tracking the prop if it changes for any
  // other reason (e.g. the parent's own onChannelUpdated-triggered refetch completing).
  const [liveBusinessRules, setLiveBusinessRules] = useState(channelBusinessRules);
  useEffect(() => {
    setLiveBusinessRules(channelBusinessRules);
  }, [channelBusinessRules]);

  // FIX 11b (frontend handover) — null whenever the Business Rules Workflow tab isn't the one
  // currently mounted (only the active tab panel renders its content below), which doubles as
  // the "nothing to guard" case for every helper that reads it.
  const workflowCanvasTabRef = useRef<WorkflowCanvasTabHandle>(null);



  const applyInitialTab = useCallback(() => {
    const resolvedId = resolveInitialTabId({
      initialTabId: initialOpenAddSkill ? 'skills' : initialTabId,
      initialTab,
      isChannelAdmin,
    });
    setActiveTabIndex(getTabIndexById(visibleTabs, resolvedId));
    if (initialOpenConfigEdit && resolvedId === 'config') {
      setOpenConfigEditRequest(true);
    }
  }, [initialTabId, initialTab, isChannelAdmin, visibleTabs, initialOpenConfigEdit, initialOpenAddSkill]);



  // FIX 11b — every way the active tab can change (tab bar click, or a tab's own "go to X"
  // action) routes through here, so leaving the Business Rules Workflow tab with a pending
  // structural edit always asks first, regardless of which control triggered the switch.
  const changeActiveTab = useCallback((newIndex: number) => {
    const proceed = () => setActiveTabIndex(newIndex);
    if (workflowCanvasTabRef.current) {
      workflowCanvasTabRef.current.guardNavigation(proceed);
    } else {
      proceed();
    }
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newIndex: number) => {
    changeActiveTab(newIndex);
  };



  const navigateToTab = (tabId: ChannelSettingsTabId) => {
    changeActiveTab(getTabIndexById(visibleTabs, tabId));
  };
  const handleOpenChannelConfigEdit = () => {
    navigateToTab('config');
    setOpenConfigEditRequest(true);
  };

  // FIX 11b — the drawer's own close (Toolbar's X button, and Drawer's own onClose e.g. Escape
  // key) is a navigation point exactly like a tab switch; guarded the same way, falling straight
  // through to the real onClose when the Workflow tab isn't the one currently mounted/dirty.
  const handleDrawerClose = useCallback(() => {
    if (workflowCanvasTabRef.current) {
      workflowCanvasTabRef.current.guardNavigation(onClose);
    } else {
      onClose();
    }
  }, [onClose]);



  // Resolve initial tab when drawer opens or admin visibility changes
  useEffect(() => {
    if (!open || adminLoading) return;
    const timer = setTimeout(applyInitialTab, 0);
    return () => clearTimeout(timer);
  }, [open, adminLoading, applyInitialTab]);



  // Clamp active index if visible tab list shrinks (e.g. non-admin)
  useEffect(() => {
    if (activeTabIndex >= visibleTabs.length) {
      setActiveTabIndex(Math.max(0, visibleTabs.length - 1));
    }
  }, [activeTabIndex, visibleTabs.length]);



  // Directly manipulate Modal root to allow sidebar clicks
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        const modalRoots = document.querySelectorAll('.MuiModal-root');
        modalRoots.forEach((modalRoot) => {
          const drawer = modalRoot.querySelector('.channel-settings-drawer');
          if (drawer) {
            (modalRoot as HTMLElement).style.pointerEvents = 'none';
            (drawer as HTMLElement).style.pointerEvents = 'auto';
            const drawerPaper = modalRoot.querySelector('.MuiDrawer-paper') as HTMLElement;
            if (drawerPaper) {
              drawerPaper.style.pointerEvents = 'auto';
            }
          }
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);



  const renderTabContent = (tabId: ChannelSettingsTabId) => {
    switch (tabId) {
      case 'config':
        return (
          <EmailConfigTab
            channelId={channelId}
            channelName={channelName}
            channelDescription={channelDescription}
            channelTags={channelTags}
            channelSubtags={channelSubtags}
            channelBusinessRules={liveBusinessRules}
            isChannelArchived={isChannelArchived}
            channelType={channelType}
            workspaceTag={workspaceTag}
            onClose={onClose}
            openEditModalRequest={openConfigEditRequest}
            onOpenEditModalRequestHandled={() => setOpenConfigEditRequest(false)}
            onChannelUpdated={onChannelUpdated}
            onBuildWorkflow={() => navigateToTab('workflow')}
          />
        );
      case 'members':
        return (
          <MembersTab
            channelId={channelId}
            channelName={channelName || LanguageConstants.topic}
            isChannelArchived={isChannelArchived}
          />
        );
      case 'apps':
        return (
          <AppsTab
            channelId={channelId}
            channelName={channelName}
            isChannelArchived={isChannelArchived}
          />
        );
      case 'skills':
        return (
          <SkillsTab
            channelId={channelId}
            isChannelArchived={isChannelArchived}
            workspaceTag={workspaceTag}
            initialOpenAddSkill={initialOpenAddSkill}
            onInitialAddSkillOpened={onInitialAddSkillOpened}
          />
        );
      case 'notifications':
        return <NotificationsTab channelId={channelId} workspaceId={workspaceId} />;
      case 'automation':
        return (
          <AutomationTab
            channelId={channelId}
            channelSubtags={channelSubtags}
            onNavigateToChannelConfig={() => navigateToTab('config')}
            onOpenChannelConfigEdit={handleOpenChannelConfigEdit}
          />
        );
      case 'workflow':
        return (
          <WorkflowErrorBoundary>
            <WorkflowCanvasTab
              ref={workflowCanvasTabRef}
              channelId={channelId}
              channelBusinessRules={liveBusinessRules}
              onBusinessRulesChanged={(updatedText) => {
                setLiveBusinessRules(updatedText);
                onChannelUpdated?.();
              }}
              onOpenDashboard={onOpenDashboard}
            />
          </WorkflowErrorBoundary>
        );
      case 'channelWorkflow':
        return (
          <WorkflowErrorBoundary>
            <LegacyWorkflowCanvasTab channelId={channelId} />
          </WorkflowErrorBoundary>
        );
      default:
        return null;
    }
  };



  return (
    <>
      <style>
        {`
          body.sidebar-closed .channel-settings-drawer .MuiDrawer-paper {
            width: calc(100% - 60px) !important;
            left: 60px !important;
          }
          body.sidebar-open .channel-settings-drawer .MuiDrawer-paper {
            width: calc(100% - 240px) !important;
            left: 240px !important;
          }
          .MuiModal-root:has(.channel-settings-drawer) .MuiBackdrop-root,
          .MuiModal-root .channel-settings-drawer ~ .MuiBackdrop-root {
            display: none !important;
          }
          .MuiModal-root:has(.channel-settings-drawer),
          .MuiModal-root[class*="channel-settings"] {
            pointer-events: none !important;
          }
          body.sidebar-closed .MuiModal-root:has(.channel-settings-drawer) {
            left: 60px !important;
            width: calc(100% - 60px) !important;
            clip-path: inset(0 0 0 0);
          }
          body.sidebar-open .MuiModal-root:has(.channel-settings-drawer) {
            left: 240px !important;
            width: calc(100% - 240px) !important;
            clip-path: inset(0 0 0 0);
          }
          .MuiModal-root:has(.channel-settings-drawer) .channel-settings-drawer,
          .MuiModal-root .channel-settings-drawer {
            pointer-events: auto !important;
          }
          .MuiModal-root:has(.channel-settings-drawer) .MuiDrawer-paper,
          .MuiModal-root .channel-settings-drawer .MuiDrawer-paper {
            pointer-events: auto !important;
          }
          body:has(.channel-settings-drawer) .MuiDrawer-root[class*="MuiDrawer-permanent"] {
            z-index: 1400 !important;
          }
        `}
      </style>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        hideBackdrop={true}
        className="channel-settings-drawer"
        ModalProps={{
          disableEnforceFocus: true,
          disableAutoFocus: true,
          disableRestoreFocus: true,
          style: {
            pointerEvents: 'none',
          },
        }}
        sx={{
          zIndex: 1300,
          left: '60px',
          width: 'calc(100% - 60px)',
          pointerEvents: 'auto',
          '& .MuiBackdrop-root': {
            display: 'none !important',
          },
          '& .MuiDrawer-paper': {
            width: 'calc(100% - 60px)',
            height: '100%',
            // Toolbar/Tabs/content below were plain stacked block children — no flex column, so
            // the content area's height was never anything but "however tall its own content
            // happens to be." A percentage height (or centering) anywhere inside a tab's content
            // had nothing real to resolve against, no matter how deep the fix was applied inside
            // that tab.
            display: 'flex',
            flexDirection: 'column',
            left: '0 !important',
            right: 'auto !important',
            backgroundColor: muiTheme.palette.background.default,
            zIndex: 1300,
            pointerEvents: 'auto',
          },
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1,
            borderBottom: `1px solid ${muiTheme.palette.divider}`,
            backgroundColor: muiTheme.palette.background.paper,
            position: 'sticky',
            top: 0,
            zIndex: 1301,
            minHeight: '64px !important',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: muiTheme.palette.text.primary }}>
              Settings
            </Typography>
            {channelName && (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Channel: <Box component="span" sx={{ fontWeight: 600 }}>{channelName}</Box>
                {workspaceTag && (
                  <>
                    <Box component="span" sx={{ ml: '5px' }}>|</Box>
                    <Box component="span">
                      Workspace: <Box component="span" sx={{ fontWeight: 600 }}>{workspaceTag}</Box>
                    </Box>
                  </>
                )}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleDrawerClose} size="small" aria-label="Close settings">
            <CloseIcon />
          </IconButton>
        </Toolbar>



        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTabIndex}
            onChange={handleTabChange}
            aria-label="Channel settings tabs"
            sx={{
              px: 3,
              '& .MuiTabs-indicator': {
                backgroundColor: muiTheme.palette.primary.main,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 400,
                color: muiTheme.palette.text.secondary,
                '&.Mui-selected': {
                  color: muiTheme.palette.text.primary,
                  fontWeight: 600,
                },
                '&:focus-visible': {
                  outline: `2px solid ${muiTheme.palette.primary.main}`,
                  outlineOffset: 2,
                },
              },
            }}
          >
            {visibleTabs.map((tab, index) => (
              <Tab
                key={tab.id}
                id={tabButtonId(tab.id)}
                label={tab.label}
                aria-controls={tabPanelId(tab.id)}
                tabIndex={activeTabIndex === index ? 0 : -1}
              />
            ))}
          </Tabs>
        </Box>



        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
          {visibleTabs.map((tab, index) => (
            <Box
              key={tab.id}
              role="tabpanel"
              id={tabPanelId(tab.id)}
              aria-labelledby={tabButtonId(tab.id)}
              hidden={activeTabIndex !== index}
              // height: '100%' only actually means something now that the Paper above is a real
              // flex column and this box's own parent has minHeight: 0 — previously this was a
              // plain display: 'block' box, sized to its own content with nothing to stretch
              // against, which is why a tab's own height:'100%'/centering never worked no matter
              // what was changed inside that tab.
              sx={{ display: activeTabIndex === index ? 'block' : 'none', height: '100%' }}
            >
              {activeTabIndex === index && renderTabContent(tab.id)}
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );
};



export default ChannelSettings;
